import { Component, Input, Output, EventEmitter, ContentChildren, QueryList, AfterContentInit } from '@angular/core';
import { TabPanelComponent } from './tab-panel.component';

export type TabStyle = 'tabs' | 'pills' | 'custom';
export type TabVariant = 'default' | 'justified' | 'fill';

/**
 * Tabs Component
 *
 * A wrapper component for creating tabbed interfaces with consistent styling.
 *
 * Usage:
 * ```html
 * <!-- Basic tabs -->
 * <app-tabs>
 *   <app-tab-panel id="home" title="Home">
 *     <p>Home content...</p>
 *   </app-tab-panel>
 *   <app-tab-panel id="profile" title="Profile">
 *     <p>Profile content...</p>
 *   </app-tab-panel>
 * </app-tabs>
 *
 * <!-- Pills style, justified -->
 * <app-tabs tabStyle="pills" variant="justified">
 *   <app-tab-panel id="tab1" title="Tab 1" icon="home">Content 1</app-tab-panel>
 *   <app-tab-panel id="tab2" title="Tab 2" icon="user">Content 2</app-tab-panel>
 * </app-tabs>
 *
 * <!-- Vertical tabs -->
 * <app-tabs [vertical]="true" tabStyle="pills">
 *   <app-tab-panel id="tab1" title="Settings">...</app-tab-panel>
 *   <app-tab-panel id="tab2" title="Profile">...</app-tab-panel>
 * </app-tabs>
 * ```
 */
@Component({
  selector: 'app-tabs',
  standalone: false,
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.scss'
})
export class TabsComponent implements AfterContentInit {
  /** Style of tabs: tabs, pills, or custom */
  @Input() tabStyle: TabStyle = 'tabs';

  /** Variant: default, justified, or fill */
  @Input() variant: TabVariant = 'default';

  /** Whether tabs should be displayed vertically */
  @Input() vertical = false;

  /** Position of vertical tabs: left or right */
  @Input() verticalPosition: 'left' | 'right' = 'left';

  /** Active tab ID */
  @Input() activeTab = '';

  /** Whether to add background to tabs (for pills) */
  @Input() background = false;

  /** Emits when active tab changes */
  @Output() tabChange = new EventEmitter<string>();

  @ContentChildren(TabPanelComponent) tabPanels!: QueryList<TabPanelComponent>;

  currentTab = '';

  ngAfterContentInit(): void {
    // Set initial active tab
    if (this.activeTab) {
      this.currentTab = this.activeTab;
    } else if (this.tabPanels.length > 0) {
      this.currentTab = this.tabPanels.first.id;
    }

    // Subscribe to changes in tab panels
    this.tabPanels.changes.subscribe(() => {
      if (!this.tabPanels.find(t => t.id === this.currentTab)) {
        this.currentTab = this.tabPanels.first?.id || '';
      }
    });
  }

  get navClass(): string {
    const classes: string[] = ['nav'];

    // Style classes
    if (this.tabStyle === 'tabs') {
      classes.push('nav-tabs');
    } else if (this.tabStyle === 'pills') {
      classes.push('nav-pills');
    } else if (this.tabStyle === 'custom') {
      classes.push('nav-tabs', 'nav-tabs-custom');
    }

    // Variant classes
    if (this.variant === 'justified') {
      classes.push('nav-justified');
    } else if (this.variant === 'fill') {
      classes.push('nav-fill');
    }

    // Vertical classes
    if (this.vertical) {
      classes.push('flex-column');
    }

    // Background
    if (this.background) {
      classes.push('bg-light');
    }

    return classes.join(' ');
  }

  selectTab(tabId: string): void {
    const tab = this.tabPanels.find(t => t.id === tabId);
    if (tab && !tab.disabled) {
      this.currentTab = tabId;
      this.tabChange.emit(tabId);
    }
  }

  isActive(tabId: string): boolean {
    return this.currentTab === tabId;
  }
}
